import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbListHelper } from "abweb/collection/AbListHelper";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { KeyCode, KeyValue } from "abweb/utils/KeyboardUtils";
import { focusable } from "tabbable";
import { Orientation } from "../SharedTypes";
export class KeyboardUtilsReact {
  constructor() {
    // static class, can't create an instance
  }

  /**
   * Returns whether the KeyboardEvent represents a Backspace key.
   * @param event
   * @returns {boolean}
   */
  static isBackspaceKey(event) {
    // Note that according to MDN documentation use of the keyCode and which property is deprecated in favor of key. Go figure.
    return event.key === KeyValue.BACKSPACE;
  }

  /**
   * Returns whether the KeyboardEvent represents an unmodified backspace gesture.
   * Eg, as opposed to Shift+Backspace or having other modifiers added.
   */
  static isPlainBackspaceKey(e) {
    return e.key === KeyValue.BACKSPACE && !e.ctrlKey && !e.altKey && !e.metaKey && e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents the exact combination of
   * ctrl alt shift and f11
   */
  static isCtrlAltShiftF11(e) {
    return e.keyCode === KeyCode.F11 && e.ctrlKey && e.altKey && !e.metaKey && e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a Shift+Enter gesture.
   * Eg, as opposed to Ctrl+Shift+Enter or having other modifiers added.
   */
  static isShiftEnterGesture(e) {
    return e.keyCode === KeyCode.CR && !e.ctrlKey && !e.altKey && !e.metaKey && e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified Enter key gesture.
   * Eg, as opposed to Ctrl+Shift+Enter or having other modifiers added.
   */
  static isPlainEnterGesture(e) {
    return e.keyCode === KeyCode.CR && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified Tab key gesture.
   * Eg, as opposed to Ctrl+Shift+Enter or having other modifiers added.
   */
  static isPlainTabGesture(e) {
    return e.keyCode === KeyCode.TAB && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified ctrl+s
   * That is, with no additional modifier keys added.
   */
  static isPlainControlS(e) {
    return e.keyCode === KeyCode.LETTER_S && e.ctrlKey === true && !e.altKey && !e.metaKey && !e.shiftKey;
  }
  static isPlainDelete(e) {
    return e.key === "Delete" && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified ctrl+z
   * That is, with no additional modifier keys added.
   */
  static isPlainControlZ(e) {
    return e.keyCode === KeyCode.LETTER_Z && e.ctrlKey === true && !e.altKey && !e.metaKey && !e.shiftKey;
    // TODO - figure out if there are advantages to using getModifierState instead of the event flags.
    // I see that getModifierState throws when events are simulated in testing via ReactTestUtils.Simulate.keyDown
    // return (e.keyCode === KeyCode.LETTER_Z &&
    //     e.getModifierState(KeyModifier.Control) === true &&
    //     !e.getModifierState(KeyModifier.Alt) &&
    //     !e.getModifierState(KeyModifier.Meta) &&
    //     !e.getModifierState(KeyModifier.Shift));
  }
  static isPlainControlShiftZ(e) {
    return e.key === "Z" && e.ctrlKey && e.shiftKey && !e.altKey && !e.metaKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified ctrl+y
   * That is, with no additional modifier keys added.
   */
  static isPlainControlY(e) {
    return e.keyCode === KeyCode.LETTER_Y && e.ctrlKey === true && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified Esc key gesture.
   * Eg, as opposed to Ctrl+Shift+ or having other modifiers added.
   */
  static isPlainEscGesture(e) {
    return e.keyCode === KeyCode.ESC && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified down arrow key gesture.
   * Eg, as opposed to Ctrl+Shift+ or having other modifiers added.
   */
  static isPlainDownArrow(e) {
    return e.keyCode === KeyCode.DOWN && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified up arrow key gesture.
   * Eg, as opposed to Ctrl+Shift+ or having other modifiers added.
   */
  static isPlainUpArrow(e) {
    return e.keyCode === KeyCode.UP && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a ctrl+space, without additional modifiers.
   */
  static isCtrlSpace(e) {
    return e.key === " " && e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a plain unmodified Esc key gesture.
   * Eg, as opposed to Ctrl+Shift+ or having other modifiers added.
   */
  static isAsterisk(e) {
    // note that an asterisk may or may not have the shift key down,
    // depending whether it was shift+8, or numpad input
    return e.key === "*" && !e.ctrlKey && !e.altKey && !e.metaKey /* && !e.shiftKey */;
  }

  /**
   * Returns whether the KeyboardEvent represents a numpad Add (+) or not.
   * @param event
   */
  static isNumpadAdd(event) {
    if (event.nativeEvent.code) {
      return event.nativeEvent.code === "NumpadAdd";
    } else {
      // MS Edge does not support the code value
      return event.keyCode === 107;
    }
  }

  /**
   * Returns whether the KeyboardEvent represents a numpad Subtract (-) or not.
   * @param event
   */
  static isNumpadSubtract(event) {
    if (event.nativeEvent.code) {
      return event.nativeEvent.code === "NumpadSubtract";
    } else {
      // MS Edge does not support the code value
      return event.keyCode === 109;
    }
  }

  /**
   * Returns whether the KeyboardEvent represents a Shift + minus, without additional modifiers
   * @param event
   */
  static isShiftMinus(e) {
    return e.key === "_" && !e.ctrlKey && !e.altKey && !e.metaKey && e.shiftKey;
  }

  /**
   * Returns whether the KeyboardEvent represents a Shift + plus, without additional modifiers
   * @param event
   */
  static isShiftPlus(e) {
    return e.key === "+" && !e.ctrlKey && !e.altKey && !e.metaKey && e.shiftKey;
  }
  static getCharCodeFromKeyboardEvent(event) {
    let charCode;
    if (event.which == null) {
      if (event.charCode !== 0) {
        charCode = event.charCode;
      } else {
        charCode = event.keyCode;
      }
    } else {
      charCode = event.which;
    }
    return charCode;
  }

  /**
   * Create one or more key handlers for one or more key codes.
   *
   * @param handlers Array with 2 or 3 elements.
   *   - First element: Key codes. Key code or list of key codes to handle
   *   - Second element: Callback. Function to be called when key code is seen
   *   - Third element (optional): Allow browser execution to continue.
   *     Boolean or function that returns a boolean. Default false.
   *     If true or returns true, do NOT call e.preventDefault() and e.stopPropagation().
   *     Otherwise, calls e.preventDefault() and e.stopPropagation().
   */
}
_defineProperty(KeyboardUtilsReact, "makeKeyHandler", handlers => {
  const handlerMap = new Map();
  handlers.forEach(handler => {
    if (!handler) return;
    const keys = ArrayUtils.isArrayLike(handler[0]) ? handler[0] : [handler[0]];
    keys.forEach(key => {
      handlerMap.set(key, {
        cb: handler[1],
        allowContinueArg: handler[2]
      });
    });
  });
  return e => {
    // Do nothing if there's no handler
    const handlerValue = handlerMap.get(e.key);
    if (handlerValue === undefined) return;
    const handler = handlerValue.cb;
    if (handler) {
      handler(e);

      // Compute whether to allow browser default behavior & event propagation
      const allowContinueArg = handlerValue.allowContinueArg;
      let allowContinue = false;
      if (allowContinueArg !== undefined) {
        if (typeof allowContinueArg == "boolean") {
          allowContinue = allowContinueArg;
        } else {
          allowContinue = allowContinueArg(e);
        }
      }
      if (!allowContinue) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
});
_defineProperty(KeyboardUtilsReact, "arrowGroupKeyDown", (event, orientation, onFocus, preventDefault) => {
  const focusableElements = AbListHelper.from(focusable(event.currentTarget));
  if (focusableElements.size === 0) return; // Nothing to focus
  const containerFocused = event.currentTarget === document.activeElement;
  const currentIndex = document.activeElement ? focusableElements.findIndexOf(document.activeElement) : -1;
  if (currentIndex === -1 && !containerFocused) return; // Focus isn't in a place that makes sense (item in container or container)
  const focusIndex = index => {
    const modIndex = index % focusableElements.size;
    const safeIndex = modIndex < 0 ? modIndex + focusableElements.size : modIndex;
    const element = focusableElements.itemAt(safeIndex);
    element.focus();
    preventDefault && event.preventDefault();
    onFocus?.(element, safeIndex);
  };
  const forwardArrow = orientation === Orientation.HORIZONTAL ? KeyValue.ARROW_RIGHT : KeyValue.ARROW_DOWN;
  const backArrow = orientation === Orientation.HORIZONTAL ? KeyValue.ARROW_LEFT : KeyValue.ARROW_UP;
  if (containerFocused) {
    switch (event.key) {
      case forwardArrow:
      case KeyValue.HOME:
        focusIndex(0);
        break;
      case backArrow:
      case KeyValue.END:
        focusIndex(-1);
        break;
    }
  } else {
    switch (event.key) {
      case forwardArrow:
        focusIndex(currentIndex + 1);
        break;
      case KeyValue.HOME:
        focusIndex(0);
        break;
      case backArrow:
        focusIndex(currentIndex - 1);
        break;
      case KeyValue.END:
        focusIndex(-1);
        break;
    }
  }
});
//# sourceMappingURL=KeyboardUtilsReact.js.map
